import React, { useState, useEffect, useRef } from 'react';
import Lottie from 'lottie-react';
import FlipCounter from './FlipCounter';
import './FullScreenLoadingView.css';

const FullScreenLoadingView = ({ documentTitle, cardSubtitle, onComplete, onBack, onLogoClick }) => {
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [copied, setCopied] = useState(false);
  const [completeIconAnimation, setCompleteIconAnimation] = useState(null);
  const [favoritesIconAnimation, setFavoritesIconAnimation] = useState(null);
  const [saveIconAnimation, setSaveIconAnimation] = useState(null);
  const [aiIconAnimation, setAiIconAnimation] = useState(null);
  const [footerIconAnimation, setFooterIconAnimation] = useState(null);
  const [marqueeStarted, setMarqueeStarted] = useState(false);
  const videoRef = useRef(null);
  const mainRef = useRef(null);
  const pageRef = useRef(null);
  const marqueeRef = useRef(null);
  const favoritesLottieRef = useRef(null);
  const saveLottieRef = useRef(null);
  const aiLottieRef = useRef(null);
  const footerLottieRef = useRef(null);

  useEffect(() => {
    fetch('/complete-icon.json')
      .then(res => res.json())
      .then(data => setCompleteIconAnimation(data));
    fetch('/favorites-icon.json')
      .then(res => res.json())
      .then(data => setFavoritesIconAnimation(data));
    fetch('/save-icon.json')
      .then(res => res.json())
      .then(data => setSaveIconAnimation(data));
    fetch('/ai-icon.json')
      .then(res => res.json())
      .then(data => setAiIconAnimation(data));
    fetch('/footer-icon.json')
      .then(res => res.json())
      .then(data => setFooterIconAnimation(data));
  }, []);

  // Loop bento animations every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (favoritesLottieRef.current) {
        favoritesLottieRef.current.goToAndPlay(0);
      }
      if (saveLottieRef.current) {
        saveLottieRef.current.goToAndPlay(0);
      }
      if (aiLottieRef.current) {
        aiLottieRef.current.goToAndPlay(0);
      }
      if (footerLottieRef.current) {
        footerLottieRef.current.goToAndPlay(0);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const startTime = Date.now();
    const totalDuration = 5000; // 5 seconds total

    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / totalDuration) * 100, 100);
      setProgress(newProgress);

      if (newProgress >= 100) {
        clearInterval(progressInterval);
        setIsComplete(true);
      }
    }, 50);

    return () => clearInterval(progressInterval);
  }, []);


  useEffect(() => {
    const handleScroll = () => {
      // Start marquee animation when it comes into view
      if (marqueeRef.current && !marqueeStarted) {
        const marqueeRect = marqueeRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        if (marqueeRect.top < viewportHeight) {
          setMarqueeStarted(true);
        }
      }
    };

    const mainElement = mainRef.current;
    if (mainElement) {
      mainElement.addEventListener('scroll', handleScroll);
      return () => mainElement.removeEventListener('scroll', handleScroll);
    }
  }, [marqueeStarted]);

  const handleCopyLink = () => {
    const shareUrl = window.location.origin + window.location.pathname;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLinkedInShare = () => {
    const shareUrl = window.location.origin + window.location.pathname;
    const text = `Just created an amazing presentation with @Templafy's AI. The future of document creation is here!`;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}&summary=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <div className="fullscreen-loading" ref={pageRef}>

      {/* Left Sidebar - same as workspace */}
      <div className="loading-sidebar">
        <div className="loading-sidebar-header">
          <img src="/Templafy-logo-white.svg" alt="Templafy" className="loading-sidebar-logo" onClick={onLogoClick} style={{ cursor: 'pointer' }} />
        </div>

        <div className="loading-share-section">
          <h3 className="loading-share-title">Share what you've seen</h3>
          <p className="loading-share-text">
            Help your team discover a better way to create documents.
          </p>
          <div className="loading-share-buttons">
            <button className="loading-share-button linkedin" onClick={handleLinkedInShare}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M16 1H4a3 3 0 00-3 3v12a3 3 0 003 3h12a3 3 0 003-3V4a3 3 0 00-3-3zM6 16H4V7h2v9zM5 6a1 1 0 110-2 1 1 0 010 2zm11 10h-2v-4.5c0-1.1-.9-2-2-2s-2 .9-2 2V16H8V7h2v1c.6-.6 1.5-1 2.5-1 1.9 0 3.5 1.6 3.5 3.5V16z" fill="currentColor"/>
              </svg>
              Share on LinkedIn
            </button>
            <button className="loading-share-button copy" onClick={handleCopyLink}>
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="M8.995,7.005 L8.995,7.005c1.374,1.374,1.374,3.601,0,4.975l-1.99,1.99c-1.374,1.374-3.601,1.374-4.975,0l0,0c-1.374-1.374-1.374-3.601,0-4.975 l1.748-1.698"/>
                <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="M7.005,8.995 L7.005,8.995c-1.374-1.374-1.374-3.601,0-4.975l1.99-1.99c1.374-1.374,3.601-1.374,4.975,0l0,0c1.374,1.374,1.374,3.601,0,4.975 l-1.748,1.698"/>
              </svg>
              {copied ? 'Copied!' : 'Copy link'}
            </button>
          </div>
        </div>
      </div>

      {/* Top Header - always visible */}
      <div className="loading-sticky-header visible">
        <div className="sticky-header-left">
          <h1 className="sticky-header-title">{documentTitle}</h1>
        </div>
        <button className="back-to-overview" onClick={onBack}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 2v6h-6"></path>
            <path d="M3 12a9 9 0 0 1 15-6.7L21 8"></path>
            <path d="M3 22v-6h6"></path>
            <path d="M21 12a9 9 0 0 1-15 6.7L3 16"></path>
          </svg>
          Try another document
        </button>
      </div>

      {/* Main Content - scrollable area */}
      <div className="loading-main" ref={mainRef}>
        <div className="loading-content">
          {/* Inline Loader */}
          <div className={`inline-loader ${isComplete ? 'complete' : ''}`}>
            <div className="inline-loader-icon">
              {isComplete ? (
                completeIconAnimation && (
                  <Lottie
                    animationData={completeIconAnimation}
                    loop={false}
                    className="inline-loader-complete-icon"
                  />
                )
              ) : (
                <img src="/Loader-small.gif" alt="Loading" />
              )}
            </div>
            <div className="inline-loader-content">
              <h3 className="inline-loader-title">
                {isComplete ? 'Presentation ready' : 'Preparing presentation'}
              </h3>
              <p className="inline-loader-subtitle">
                {isComplete ? 'Download now and see the result' : 'Hang tight, this won\'t take long'}
              </p>
            </div>
            {!isComplete && (
              <div className="inline-loader-progress">
                <div className="inline-loader-progress-fill" style={{ width: `${progress}%` }} />
              </div>
            )}
            {isComplete && (
              <button className="inline-loader-download">
                Download presentation
              </button>
            )}
          </div>

          {/* Hero Section */}
          <div className="right-hero">
            <div className="right-hero-title-container">
              <h1 className="right-hero-title">Less effort.</h1>
              <h1 className="right-hero-title blue">Better outcomes.</h1>
            </div>
          </div>

          {/* Features Section */}
          <div className="right-features">
            <div className="features-bento">
              {/* Top row */}
              <div className="bento-card bento-counter-card bento-top-right">
                <div className="bento-counter-overlay">
                  <FlipCounter />
                </div>
                <p className="bento-counter-description">
                  You've seen this part, want to see the rest? Templafy helps teams work faster, stay aligned, and deliver better results. 4 million+ users are already using the full platform.
                </p>
                <a className="bento-counter-button" href="https://www.templafy.com/home/book-a-demo/" target="_blank" rel="noopener noreferrer">
                  Book a demo now
                </a>
              </div>

              {/* Bottom row */}
              <a href="https://www.templafy.com/home/platform/brand-content-distribution/" target="_blank" rel="noopener noreferrer" className="bento-card bento-light bento-bottom-left bento-link-card">
                <p className="bento-label">Your favorite organizational content</p>
                <div className="bento-footer">
                  <span className="bento-footer-text">Ready to use in the apps you use</span>
                  <div className="bento-icon">
                    {favoritesIconAnimation && (
                      <Lottie
                        animationData={favoritesIconAnimation}
                        loop={false}
                        lottieRef={favoritesLottieRef}
                        className="bento-lottie-icon"
                      />
                    )}
                  </div>
                </div>
              </a>
              <a href="https://www.templafy.com/home/platform/automated-template-management/" target="_blank" rel="noopener noreferrer" className="bento-card bento-sage bento-bottom-mid bento-link-card">
                <p className="bento-label">Save your best work for future use</p>
                <div className="bento-footer">
                  <span className="bento-footer-text">No more browsing for missing slides</span>
                  <div className="bento-icon">
                    {saveIconAnimation && (
                      <Lottie
                        animationData={saveIconAnimation}
                        loop={false}
                        lottieRef={saveLottieRef}
                        className="bento-lottie-icon"
                      />
                    )}
                  </div>
                </div>
              </a>
              <a href="https://www.templafy.com/home/platform/#edit" target="_blank" rel="noopener noreferrer" className="bento-card bento-dark bento-bottom-right bento-link-card">
                <p className="bento-label">Powerful one-click AI text actions</p>
                <div className="bento-footer">
                  <span className="bento-footer-text">No need to describe what you need</span>
                  <div className="bento-icon">
                    {aiIconAnimation && (
                      <Lottie
                        animationData={aiIconAnimation}
                        loop={false}
                        lottieRef={aiLottieRef}
                        className="bento-lottie-icon bento-lottie-white"
                      />
                    )}
                  </div>
                </div>
              </a>
            </div>
          </div>

          {/* Blue Marquee Banner */}
          <div className={`blue-marquee-banner ${marqueeStarted ? 'animating' : ''}`} ref={marqueeRef}>
            <div className="blue-marquee-track">
              <span className="blue-marquee-text">Watch how your new favorite app works.</span>
              <span className="blue-marquee-text">Watch how your new favorite app works.</span>
              <span className="blue-marquee-text">Watch how your new favorite app works.</span>
            </div>
          </div>

          {/* Video Section */}
          <div className="right-video" ref={videoRef}>
            <iframe
              title="Video Player"
              src="https://video.templafy.com/v.ihtml/player.html?token=0292bfa781ace0dc391b0d2b795f329a&source=embed&photo%5fid=115219126"
              frameBorder="0"
              scrolling="no"
              allowFullScreen
              allow="autoplay; fullscreen"
            />
          </div>

          {/* Trust Logos - last content section with divider */}
          <div className="trust-section visible">
            <p className="trust-label">Trusted by the most trusted, e.g.</p>
            <div className="trust-logos">
              <img src="/BDO_Logo_Black.svg" alt="BDO" className="trust-logo" />
              <img src="/Philips_Logo_Black.svg" alt="Philips" className="trust-logo" />
              <img src="/Cisco_Logo_Black.svg" alt="Cisco" className="trust-logo" />
              <img src="/PwC_Logo_Black.svg" alt="PwC" className="trust-logo" />
              <img src="/KPMG_Logo_Black-1.svg" alt="KPMG" className="trust-logo" />
              <img src="/Adobe_Logo_Black.svg" alt="Adobe" className="trust-logo" />
            </div>
          </div>
        </div>

        {/* Footer revealed behind the content */}
        <div className="bottom-share">
          <div className="footer-header">
            {footerIconAnimation && (
              <div className="footer-icon">
                <Lottie
                  animationData={footerIconAnimation}
                  loop={false}
                  lottieRef={footerLottieRef}
                  className="footer-lottie-icon"
                />
              </div>
            )}
            <h2 className="footer-title">Share this experience</h2>
          </div>
          <div className="bottom-share-buttons">
            <button className="copy-link-button" onClick={handleCopyLink}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M8.995 7.005c1.374 1.374 1.374 3.601 0 4.975l-1.99 1.99c-1.374 1.374-3.601 1.374-4.975 0-1.374-1.374-1.374-3.601 0-4.975l1.748-1.698" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7.005 8.995c-1.374-1.374-1.374-3.601 0-4.975l1.99-1.99c1.374-1.374 3.601-1.374 4.975 0 1.374 1.374 1.374 3.601 0 4.975l-1.748 1.698" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {copied ? 'Copied!' : 'Copy link to document creator'}
            </button>
            <button className="share-linkedin-button" onClick={handleLinkedInShare}>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <path d="M16 1H4a3 3 0 00-3 3v12a3 3 0 003 3h12a3 3 0 003-3V4a3 3 0 00-3-3zM6 16H4V7h2v9zM5 6a1 1 0 110-2 1 1 0 010 2zm11 10h-2v-4.5c0-1.1-.9-2-2-2s-2 .9-2 2V16H8V7h2v1c.6-.6 1.5-1 2.5-1 1.9 0 3.5 1.6 3.5 3.5V16z" fill="currentColor"/>
              </svg>
              Share on LinkedIn
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullScreenLoadingView;
